import { useEffect, useRef } from 'react';
import { apiFetch, sendApiBeacon } from '../network';

const SESSION_STORAGE_KEY = 'sylvie-analytics-session-id';
const FLUSH_INTERVAL_MS = 5000;
const MAX_QUEUE_SIZE = 36;
const SCROLL_MILESTONES = [25, 50, 75, 100];
const MOUSE_SAMPLE_INTERVAL_MS = 180;
const ATTENTION_SAMPLE_INTERVAL_MS = 2200;
const ATTENTION_SETTLE_DELAY_MS = 180;
const ATTENTION_VIEWPORT_Y_RATIO = 0.38;
const ATTENTION_TARGET_SELECTOR = [
  '[data-analytics-focus]',
  'h1',
  'h2',
  'h3',
  'img',
  'picture',
  'video',
  'button',
  'a',
  'strong'
].join(', ');

function clamp(value, minimum, maximum) {
  return Math.min(Math.max(value, minimum), maximum);
}

function getAnalyticsDeviceType() {
  if (typeof window === 'undefined') {
    return 'desktop';
  }

  const hasTouchPoints = typeof navigator !== 'undefined' && Number(navigator.maxTouchPoints || 0) > 0;
  const coarsePointer = typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches;

  return hasTouchPoints || coarsePointer || window.innerWidth <= 860 ? 'mobile' : 'desktop';
}

function createSessionId() {
  if (typeof window !== 'undefined' && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `session-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getSessionId() {
  if (typeof window === 'undefined') {
    return 'server';
  }

  const currentSessionId = window.sessionStorage.getItem(SESSION_STORAGE_KEY);

  if (currentSessionId) {
    return currentSessionId;
  }

  const nextSessionId = createSessionId();
  window.sessionStorage.setItem(SESSION_STORAGE_KEY, nextSessionId);
  return nextSessionId;
}

function normalizeLabel(value, fallback) {
  if (typeof value !== 'string') {
    return fallback;
  }

  const normalizedValue = value.replace(/\s+/g, ' ').trim();
  return normalizedValue.length > 0 ? normalizedValue.slice(0, 140) : fallback;
}

function getInteractiveLabel(node) {
  if (!node) {
    return 'interaction';
  }

  const dataLabel = normalizeLabel(node.getAttribute('data-analytics-label'), '');
  if (dataLabel) {
    return dataLabel;
  }

  const ariaLabel = normalizeLabel(node.getAttribute('aria-label'), '');
  if (ariaLabel) {
    return ariaLabel;
  }

  const title = normalizeLabel(node.getAttribute('title'), '');
  if (title) {
    return title;
  }

  return normalizeLabel(node.textContent, node.tagName.toLowerCase());
}

function getSectionId(node) {
  if (!node) {
    return '';
  }

  const sectionNode = node.closest('[data-analytics-section], section[id], [id]');
  if (!sectionNode) {
    return '';
  }

  return normalizeLabel(
    sectionNode.getAttribute('data-analytics-section') || sectionNode.getAttribute('id'),
    ''
  );
}

function getScrollDepth() {
  const scrollHeight = Math.max(
    document.documentElement.scrollHeight,
    document.body.scrollHeight
  );
  const viewportHeight = window.innerHeight;

  if (scrollHeight <= viewportHeight) {
    return 100;
  }

  return Math.min(
    100,
    Math.round(((window.scrollY + viewportHeight) / scrollHeight) * 100)
  );
}

function getDocumentDimensions() {
  return {
    width: Math.max(
      document.documentElement.scrollWidth,
      document.body.scrollWidth,
      window.innerWidth
    ),
    height: Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight,
      window.innerHeight
    )
  };
}

function toDocumentRatios(clientX, clientY) {
  const dimensions = getDocumentDimensions();

  return {
    xRatio: dimensions.width > 0
      ? Math.min(1, Math.max(0, (window.scrollX + clientX) / dimensions.width))
      : null,
    yRatio: dimensions.height > 0
      ? Math.min(1, Math.max(0, (window.scrollY + clientY) / dimensions.height))
      : null
  };
}

function getViewportRect(rect) {
  const left = clamp(rect.left, 0, window.innerWidth);
  const right = clamp(rect.right, 0, window.innerWidth);
  const top = clamp(rect.top, 0, window.innerHeight);
  const bottom = clamp(rect.bottom, 0, window.innerHeight);
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);

  return {
    left,
    right,
    top,
    bottom,
    width,
    height,
    area: width * height
  };
}

function getSemanticBoost(node) {
  if (!node) {
    return 1;
  }

  const tagName = node.tagName.toLowerCase();

  if (node.hasAttribute('data-analytics-focus')) {
    return 1.34;
  }

  if (tagName === 'h1') {
    return 1.36;
  }

  if (tagName === 'h2') {
    return 1.24;
  }

  if (tagName === 'h3') {
    return 1.14;
  }

  if (tagName === 'img' || tagName === 'picture' || tagName === 'video') {
    return 1.18;
  }

  if (tagName === 'button' || tagName === 'a') {
    return 1.08;
  }

  if (tagName === 'strong') {
    return 1.04;
  }

  return 1;
}

function isVisibleAnalyticsTarget(node) {
  if (!(node instanceof Element)) {
    return false;
  }

  const style = window.getComputedStyle(node);

  return style.display !== 'none' &&
    style.visibility !== 'hidden' &&
    Number(style.opacity || '1') > 0.18;
}

function getAttentionTargets() {
  const focusX = window.innerWidth * 0.5;
  const focusY = window.innerHeight * ATTENTION_VIEWPORT_Y_RATIO;
  const viewportArea = Math.max(1, window.innerWidth * window.innerHeight);
  const candidates = Array.from(document.querySelectorAll(ATTENTION_TARGET_SELECTOR))
    .filter(isVisibleAnalyticsTarget)
    .map((node) => {
      const rect = node.getBoundingClientRect();
      const viewportRect = getViewportRect(rect);

      if (viewportRect.width < 24 || viewportRect.height < 24 || viewportRect.area === 0) {
        return null;
      }

      const centerX = viewportRect.left + (viewportRect.width / 2);
      const centerY = viewportRect.top + (viewportRect.height / 2);
      const distanceX = (centerX - focusX) / Math.max(window.innerWidth, 1);
      const distanceY = (centerY - focusY) / Math.max(window.innerHeight, 1);
      const focusDistance = Math.sqrt((distanceX * distanceX) + (distanceY * distanceY));
      const areaRatio = clamp(viewportRect.area / viewportArea, 0, 1);
      const score = (
        (0.42 + (areaRatio * 2.2)) *
        (1.22 - Math.min(focusDistance, 1) * 0.88) *
        getSemanticBoost(node)
      );

      return {
        x: centerX,
        y: centerY,
        score,
        sectionId: getSectionId(node),
        label: normalizeLabel(node.getAttribute('data-analytics-label') || node.tagName.toLowerCase(), 'focus'),
        weight: clamp(score * 1.9, 0.35, 2.8)
      };
    })
    .filter(Boolean)
    .sort((left, right) => right.score - left.score);

  const uniqueTargets = [];
  const seenKeys = new Set();

  candidates.forEach((candidate) => {
    const key = `${Math.round(candidate.x / 24)}:${Math.round(candidate.y / 24)}`;

    if (seenKeys.has(key) || uniqueTargets.length >= 3) {
      return;
    }

    seenKeys.add(key);
    uniqueTargets.push(candidate);
  });

  return {
    focusX,
    focusY,
    targets: uniqueTargets
  };
}

function sendAnalyticsEvents(events, useBeacon) {
  if (!Array.isArray(events) || events.length === 0) {
    return;
  }

  const payload = JSON.stringify({ events });

  if (useBeacon && typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
    const sent = sendApiBeacon('/analytics/collect', payload);
    if (sent) {
      return;
    }
  }

  apiFetch('/analytics/collect', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: payload,
    keepalive: useBeacon
  }).catch(() => {});
}

export function useAnalyticsTracker(route, options = {}) {
  const { enabled = true } = options;
  const queueRef = useRef([]);
  const flushTimeoutRef = useRef(null);
  const attentionSettleTimeoutRef = useRef(null);
  const visibleSectionsRef = useRef(new Set());
  const scrollMilestonesRef = useRef(new Set());
  const sessionIdRef = useRef(getSessionId());
  const lastMouseSampleRef = useRef(0);
  const lastAttentionSampleRef = useRef(0);

  const scheduleFlush = (useBeacon = false) => {
    if (queueRef.current.length === 0) {
      return;
    }

    if (useBeacon) {
      const pendingEvents = queueRef.current;
      queueRef.current = [];
      sendAnalyticsEvents(pendingEvents, true);
      return;
    }

    if (flushTimeoutRef.current !== null) {
      return;
    }

    flushTimeoutRef.current = window.setTimeout(() => {
      flushTimeoutRef.current = null;

      if (queueRef.current.length === 0) {
        return;
      }

      const pendingEvents = queueRef.current;
      queueRef.current = [];
      sendAnalyticsEvents(pendingEvents, false);
    }, FLUSH_INTERVAL_MS);
  };

  const enqueueEvent = (eventItem) => {
    queueRef.current.push({
      ...eventItem,
      route,
      sessionId: sessionIdRef.current,
      deviceType: getAnalyticsDeviceType(),
      viewportWidth: window.innerWidth,
      viewportHeight: window.innerHeight,
      screenWidth: window.screen.width,
      screenHeight: window.screen.height
    });

    if (queueRef.current.length >= MAX_QUEUE_SIZE) {
      const pendingEvents = queueRef.current;
      queueRef.current = [];
      sendAnalyticsEvents(pendingEvents, false);
      return;
    }

    scheduleFlush(false);
  };

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    visibleSectionsRef.current = new Set();
    scrollMilestonesRef.current = new Set();
    lastMouseSampleRef.current = 0;
    lastAttentionSampleRef.current = 0;

    enqueueEvent({
      type: 'page_view',
      referrer: document.referrer || ''
    });

    const sectionNodes = Array.from(document.querySelectorAll('section[id], [data-analytics-section]'));
    const sectionObserver = new window.IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          const sectionId = getSectionId(entry.target);
          if (!sectionId || visibleSectionsRef.current.has(sectionId)) {
            return;
          }

          visibleSectionsRef.current.add(sectionId);
          enqueueEvent({
            type: 'section_view',
            sectionId
          });
        });
      },
      { rootMargin: '0px 0px -18% 0px', threshold: 0.35 }
    );

    sectionNodes.forEach((node) => sectionObserver.observe(node));

    const onClick = (event) => {
      if (!(event.target instanceof Element)) {
        return;
      }

      const interactiveNode = event.target.closest('a, button, summary, [role="button"], [data-analytics-label]');
      const referenceNode = interactiveNode || event.target;

      enqueueEvent({
        type: 'click',
        label: getInteractiveLabel(referenceNode),
        sectionId: getSectionId(referenceNode),
        targetTag: referenceNode.tagName.toLowerCase(),
        href: interactiveNode?.getAttribute('href') || '',
        ...toDocumentRatios(event.clientX, event.clientY)
      });
    };

    const sampleAttention = () => {
      const now = Date.now();

      if (document.hidden || now - lastAttentionSampleRef.current < ATTENTION_SAMPLE_INTERVAL_MS) {
        return;
      }

      lastAttentionSampleRef.current = now;
      const { focusX, focusY, targets } = getAttentionTargets();
      const focusNode = document.elementFromPoint(focusX, focusY);

      enqueueEvent({
        type: 'attention_view',
        label: 'attention_estimee_viewport',
        sectionId: getSectionId(focusNode),
        weight: targets.length > 0 ? 0.72 : 1.05,
        ...toDocumentRatios(focusX, focusY)
      });

      targets.forEach((target) => {
        enqueueEvent({
          type: 'attention_view',
          label: `attention_estimee_${target.label}`,
          sectionId: target.sectionId,
          weight: target.weight,
          ...toDocumentRatios(target.x, target.y)
        });
      });
    };

    const onMouseMove = (event) => {
      if (getAnalyticsDeviceType() === 'mobile') {
        return;
      }

      const now = Date.now();

      if (now - lastMouseSampleRef.current < MOUSE_SAMPLE_INTERVAL_MS) {
        return;
      }

      lastMouseSampleRef.current = now;

      enqueueEvent({
        type: 'mouse_move',
        label: 'mouse_tracking',
        ...toDocumentRatios(event.clientX, event.clientY)
      });
    };

    const onTouchStart = (event) => {
      const touch = event.touches?.[0];

      if (!touch) {
        return;
      }

      const referenceNode = event.target instanceof Element
        ? event.target.closest('a, button, summary, [role="button"], [data-analytics-label]') || event.target
        : null;

      enqueueEvent({
        type: 'touch',
        label: referenceNode ? getInteractiveLabel(referenceNode) : 'touch_tracking',
        sectionId: getSectionId(referenceNode),
        targetTag: referenceNode?.tagName?.toLowerCase() || 'touch',
        weight: referenceNode ? 1.8 : 1.2,
        ...toDocumentRatios(touch.clientX, touch.clientY)
      });
    };

    const onScroll = () => {
      const currentDepth = getScrollDepth();

      SCROLL_MILESTONES.forEach((milestone) => {
        if (currentDepth >= milestone && !scrollMilestonesRef.current.has(milestone)) {
          scrollMilestonesRef.current.add(milestone);
          enqueueEvent({
            type: 'scroll_depth',
            scrollDepth: milestone
          });
        }
      });

      if (attentionSettleTimeoutRef.current !== null) {
        window.clearTimeout(attentionSettleTimeoutRef.current);
      }

      attentionSettleTimeoutRef.current = window.setTimeout(() => {
        attentionSettleTimeoutRef.current = null;
        sampleAttention();
      }, ATTENTION_SETTLE_DELAY_MS);
    };

    const onPageHide = () => {
      scheduleFlush(true);
    };

    const attentionInterval = window.setInterval(sampleAttention, ATTENTION_SAMPLE_INTERVAL_MS);

    document.addEventListener('click', onClick, true);
    window.addEventListener('mousemove', onMouseMove, { passive: true });
    window.addEventListener('touchstart', onTouchStart, { passive: true });
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', sampleAttention);
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onPageHide);

    onScroll();
    sampleAttention();

    return () => {
      sectionObserver.disconnect();
      document.removeEventListener('click', onClick, true);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', sampleAttention);
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onPageHide);
      window.clearInterval(attentionInterval);

      if (flushTimeoutRef.current !== null) {
        window.clearTimeout(flushTimeoutRef.current);
        flushTimeoutRef.current = null;
      }

      if (attentionSettleTimeoutRef.current !== null) {
        window.clearTimeout(attentionSettleTimeoutRef.current);
        attentionSettleTimeoutRef.current = null;
      }

      if (queueRef.current.length > 0) {
        const pendingEvents = queueRef.current;
        queueRef.current = [];
        sendAnalyticsEvents(pendingEvents, true);
      }
    };
  }, [enabled, route]);
}
